import React, { useState, useEffect, useRef } from "react"
import clsx from "clsx"
import {
	Grid,
	Typography,
	Checkbox,
	ListItem
} from "@material-ui/core"

import {
	Divider,
	AccessibleDrawer
} from "@/components"
import useChatStyles from "@/pages/Attendance/Chat/styles"
import useSidebarDrawerStyles from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/styles"
import useDidMount from "@/hooks/useDidMount"

import HeaderDrawer from "@/pages/Attendance/Chat/HeaderDrawer"

import ErrorHandlerService from "@/services/ErrorHandler"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import { useGlobalStateStore } from "@/store/GlobalState"

import useSettings from "@/hooks/useSettings"
import { isSmallScreen } from "@/utils/checkDevice"
import { UserNotificationSettingsProps, UserSettingsKeysValues } from "@/protocols/settings"
import usePushNotification from "@/hooks/usePushNotification"
import HardCoded from "@/services/HardCoded"

type UserSettingsMenuProps = {
	openDrawer: boolean
	onClose: () => void
	isCollapsed: boolean
}

type PushNotificationState = Record<keyof UserNotificationSettingsProps["pushNotification"], boolean>

const UserSettingsMenu = (props: UserSettingsMenuProps) => {
	const { openDrawer, onClose, isCollapsed } = props

	const chatClasses = useChatStyles()
	const sidebarDrawerClasses = useSidebarDrawerStyles({ isCollapsed })
	const globalStateStore = useGlobalStateStore()
	const { userSettings } = useSettings()
	const isAdmin = globalStateStore.user.extra_data.is_admin_mode

	const [soundNotification, setSoundNotification] = useState(Boolean)

	const [pushState, setPushState] = useState<PushNotificationState>({ newMessage: false })
	const { pushPermissionDenied, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotification()

	const prevPermissionDenied = useRef(pushPermissionDenied)

	const {
		triggerValidation
	} = useValidation()

	const getActiveChannelIndex = function () {
		const activeChannelId = Number(globalStateStore?.currentChannel?.id)
		const activeChannelIndex = globalStateStore.channels.map(channel => channel.id).indexOf(activeChannelId)
		return activeChannelIndex
	}

	useEffect(() => {
		const wasJustGranted = prevPermissionDenied.current && !pushPermissionDenied
		prevPermissionDenied.current = pushPermissionDenied

		if (!wasJustGranted) return

		const activeChannelIndex = getActiveChannelIndex()
		const settings = globalStateStore?.channels?.[activeChannelIndex]?.settings?.notifications
		if (settings?.pushNotification?.newMessage) {
			pushSubscribe()
		}
	}, [pushPermissionDenied])

	const handleChangeSoundNotification = async () => {
		try {
			const setting: Extract<keyof UserSettingsKeysValues, "notifications"> = "notifications"
			const activeChannelIndex = getActiveChannelIndex()

			const actualNotificationSettings = globalStateStore?.channels?.[activeChannelIndex]?.settings?.[setting]
			const newSoundNotificationSetting = !(actualNotificationSettings?.soundNotification)

			setSoundNotification(newSoundNotificationSetting)

			await userSettings.updateActualUserSettings(setting, {
				soundNotification: newSoundNotificationSetting
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			triggerValidation(error as ErrorType)
		}
	}

	const handleChangePushNotification = async (key: keyof UserNotificationSettingsProps["pushNotification"]) => {
		try {
			const setting: Extract<keyof UserSettingsKeysValues, "notifications"> = "notifications"
			const activeChannelIndex = getActiveChannelIndex()

			const actualNotificationSettings = globalStateStore?.channels?.[activeChannelIndex]?.settings?.[setting]
			const newValue = !(actualNotificationSettings?.pushNotification?.[key])

			if (newValue) {
				const subscription = await pushSubscribe()
				if (!subscription) return
			} else {
				await pushUnsubscribe()
			}

			setPushState(lastState => ({
				...lastState,
				[key]: newValue
			}))

			await userSettings.updateActualUserSettings(setting, {
				pushNotification: { newMessage: newValue }
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			triggerValidation(error as ErrorType)
		}
	}

	const handleCloseDrawer = () => {
		onClose()
	}

	const getUserNotificationSettings = async () => {
		try {
			const activeChannelIndex = getActiveChannelIndex()

			const settings = globalStateStore?.channels?.[activeChannelIndex]?.settings?.notifications || await userSettings.retrieveActualUserSetting("notifications") as UserNotificationSettingsProps

			setSoundNotification(settings.soundNotification)
			setPushState({ ...settings.pushNotification })

			if (settings.pushNotification?.newMessage && "serviceWorker" in navigator && window.Notification.permission === "granted") {
				const registration = await navigator.serviceWorker.ready
				const sub = await registration.pushManager.getSubscription()
				if (!sub) {
					await pushSubscribe()
				}
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			triggerValidation(error as ErrorType)
		}
	}

	const eventKeyDownHandler = (event: KeyboardEvent) => {
		const { key } = event

		if (key === "Escape") {
			handleCloseDrawer()
		}
	}

	useDidMount(() => {
		window.addEventListener(
			"keydown",
			eventKeyDownHandler,
			{ capture: true }
		)
		getUserNotificationSettings()
	})

	return (
		<AccessibleDrawer
			id="new-chat-drawer"
			anchor="left"
			open={openDrawer}
			variant="persistent"
			onClose={handleCloseDrawer}
			onMobileBackButtonPress={handleCloseDrawer}
			classes={{
				paper: clsx(chatClasses.drawerPaper, !isSmallScreen && sidebarDrawerClasses.drawerPaperOffset)
			}}
		>
			<Grid container>
				<Grid item xs={12}>
					<HeaderDrawer
						title="Configurar notificações"
						onClose={handleCloseDrawer}
					/>
				</Grid>

				<Divider size={2} orientation="horizontal" />

				<Grid
					item
					xs
				>
					<ListItem
						button
						onClick={handleChangeSoundNotification}
					>
						<Grid item>
							<Checkbox
								checked={soundNotification}
								name="sound-setting-checkbox"
							/>
						</Grid>

						<Grid item>
							<Typography variant="h2" color="textPrimary">
								Som ao receber mensagem
							</Typography>
						</Grid>

					</ListItem>

					{!isAdmin && (
						<ListItem
							button
							disabled={pushPermissionDenied}
							onClick={() => handleChangePushNotification("newMessage")}
						>
							<Grid item>
								<Checkbox
									checked={pushState.newMessage}
									disabled={pushPermissionDenied}
									name="push-new-message-checkbox"
								/>
							</Grid>

							{HardCoded.checkFeatureFlag("canActivePushNotifications") && <Grid item>
								<Typography variant="h2" color="textPrimary">
									Notificação push ao receber mensagem
								</Typography>
								{pushPermissionDenied && (
									<Typography variant="body2" color="error">
										Permissão bloqueada no navegador. Acesse as configurações do site para permitir.
									</Typography>
								)}
							</Grid>}

						</ListItem>
					)}
				</Grid>

			</Grid>
		</AccessibleDrawer>
	)
}

export default UserSettingsMenu
